import * as React from 'react';
import { CheckoutOrigin } from 'state/selection';
import {
  CreditCardFields,
  asyncValidateCreditCardForm,
} from '@tock/shared/forms/credit-card/CreditCardForm';
import { FlowRouteComponentProps } from 'components/render-prop-components/FlowRoute';
import { RouteComponentProps } from 'react-router';
import { StoreType, useActions } from '@tock/shared/utils/connect';
import { consumer, core, safeToNumber, toJSON, toJSONArray, useAppContext } from '@tock/coretypes';
import { getFullName } from '@tock/shared/utils/patron';
import {
  loggedInPatronOptionOrdersSelector,
  loggedInPatronOriginalOptionOrdersSelector,
  partyOptionOrdersSelector,
} from 'selectors/selectionSelectors';
import { reduxForm } from 'redux-form';
import { useFeatureFlag } from '@tock/shared/utils/featureFlagContext';
import { useMountEffect } from '@tock/hooks';
import { useSelector } from 'react-redux';
import Options from 'components/business/checkout/Options';
import PaymentCardDialog from 'components/business/payment/PaymentCardDialog';
import _ from 'lodash';

const asyncValidate = (values: CreditCardFields) => {
  return asyncValidateCreditCardForm(values);
};

export interface OptionsRouteConnectProps {
  app: StoreType['app'];
  lock: StoreType['lock'];
  purchase: StoreType['purchase'];
  supplementGroups: StoreType['supplementGroups'];
  paymentCard: StoreType['paymentCard'];
  visitEditToken?: string;
  visitEditPaymentMode?: boolean;
  optionOrders: core.IOptionOrder[];
  checkoutOrigin?: CheckoutOrigin;
  basePurchaseId?: number;
  originalOptionOrders: core.IOptionOrder[];
  partyOptionOrders: core.IOptionOrder[];
}

type PassedDownProps = RouteComponentProps &
  FlowRouteComponentProps & {
    onUpdatePrice(): Promise<unknown>;
    onPurchaseSuccess?(purchaseOrder: core.PurchaseOrder): void;
    business?: consumer.IBusinessConfig;
  };
export type OptionsRouteProps = PassedDownProps;

type Props = OptionsRouteProps & ReduxFormProps<CreditCardFields>;

const OptionsRoute: React.FunctionComponent<Props> = (props) => {
  const [showCardForm, setShowCardForm] = React.useState(false);
  const { actions } = useActions();
  const connectProps = useSelector(optionsRouteMapStateToProps);
  const isStripeForwardApiEnabled = useFeatureFlag('STRIPE_FORWARD_API');
  const {
    app: { config, patron },
    purchase: { calculatedPrice },
    basePurchaseId,
    visitEditPaymentMode,
    visitEditToken,
    paymentCard,
    checkoutOrigin,
  } = connectProps;

  const { routeTo } = useAppContext();

  useMountEffect(() => {
    const token = visitEditToken;

    if (!token || !calculatedPrice) {
      actions.toast.showError('Invalid link');
      handleClose();
      return;
    }

    generateTablesidePurchaseOrder(token);

    fetchAndValidateCardsPresent();
  });

  const generateTablesidePurchaseOrder = (token: string) => {
    const isTableSidePayment = !!visitEditPaymentMode;
    const existingBasePurchaseId = basePurchaseId;
    const { history } = props;

    actions.tableSide
      .getPurchaseOrder(token)
      .then((response) => {
        const purchaseOrder = toJSON<core.PurchaseOrder>(response.payload);
        purchaseOrder.visitEditToken = token;

        // If there was an edit to the purchase, replace with the latest state.
        if (existingBasePurchaseId !== purchaseOrder.basePurchaseId) {
          return actions.purchase.calculatePrice(purchaseOrder);
        } else {
          return actions.purchase.calculatePrice(calculatedPrice as core.PurchaseOrder);
        }
      })
      .then((response) => {
        const purchaseOrder = toJSON<core.PurchaseOrder>(response.payload);
        actions.batch.runActionsInBatch(() => {
          actions.selection.setVisitEditToken(token);
          actions.selection.setVisitEditPaymentMode(isTableSidePayment);
          actions.selection.setCheckoutFlowOrigin(checkoutOrigin || 'TABLESIDE');
          actions.selection.selectBasePurchaseId(purchaseOrder.basePurchaseId);
          actions.selection.selectOptions(purchaseOrder.optionOrder);
          actions.selection.selectOriginalOptions(_.cloneDeep(purchaseOrder.optionOrder));
        });
      })
      .catch((response) => {
        const errorCode = safeToNumber(response?.error?.errorCode);
        if (errorCode === 3051) {
          // If the link has expired, show the final receipt
          actions.toast.showError(response?.error?.message);
          actions.selection.setVisitEditToken(token);
          history.replace(
            `/${config?.business?.domainName}/receipt?token=${encodeURIComponent(token)}`
          );
        } else {
          handleClose();
        }
      });
  };

  const fetchAndValidateCardsPresent = () => {
    actions.paymentCard.fetchAll().then((paymentCardsProto) => {
      const paymentCards = toJSONArray<core.PaymentCard>(paymentCardsProto.payload);
      if (!paymentCards || !paymentCards.length || paymentCards.length === 0) {
        setShowCardForm(true);
        const { initializeForm, resetForm } = props;
        resetForm();
        initializeForm({
          name: getFullName(patron) || '',
          number: '',
          expiry: '',
          cvc: '',
          zipCode: patron?.zipCode ?? '',
        });
      }
    });
  };

  const handleClose = () => {
    routeTo('/');
  };

  const createNewCard = (card: NewPaymentCard) => {
    const newCard = {
      ...card,
      deviceData: paymentCard.deviceData,
    };
    const tokenizeAndSaveMethod = isStripeForwardApiEnabled
      ? actions.paymentCard.tokenizeAndSaveCardWithStripeForwarding
      : actions.paymentCard.tokenizeAndSaveCard;
    return tokenizeAndSaveMethod(newCard)
      .then(() => actions.paymentCard.fetchAll())
      .then(() => setShowCardForm(false));
  };

  const handleCompletePaymentCard = () => {
    props.handleSubmit(createNewCard)();
  };

  return (
    <>
      <Options {...props} {...connectProps} />
      {showCardForm && (
        <PaymentCardDialog
          onSubmit={handleCompletePaymentCard}
          paymentCard={paymentCard}
          submitting={props.submitting}
          fields={props.fields}
          error={props.error}
        />
      )}
    </>
  );
};

export const optionsRouteMapStateToProps = (state: StoreType): OptionsRouteConnectProps => {
  const { app, lock, purchase, paymentCard, selection, supplementGroups } = state;
  return {
    app,
    lock,
    purchase,
    paymentCard,
    supplementGroups,
    visitEditToken: selection.visitEditToken,
    visitEditPaymentMode: selection.visitEditPaymentMode,
    checkoutOrigin: selection.checkoutFlowOrigin,
    optionOrders: loggedInPatronOptionOrdersSelector(state),
    basePurchaseId: selection.basePurchaseId,
    originalOptionOrders: loggedInPatronOriginalOptionOrdersSelector(state),
    partyOptionOrders: partyOptionOrdersSelector(state),
  };
};

export default reduxForm<PassedDownProps, CreditCardFields>({
  form: 'AddCreditCardForm',
  fields: ['name', 'number', 'expiry', 'cvc', 'zipCode', 'skipAvsCheck'],
  asyncBlurFields: ['name', 'number', 'expiry', 'cvc', 'zipCode'],
  asyncValidate,
})(OptionsRoute);
